import {Component, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {TranslocoService} from "@ngneat/transloco";
import {ActivatedRoute} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, AddUserModalComponent, AddUserResult, ConfirmationModalComponent} from "@components";
import {UserDetail, TOAST_STATE} from "@model";
import {AdministratorsService, ExportService, ToastService, UserDetailService} from "@services";
import {FindOrganisersRequest} from 'src/app/model/find-organisers-request';
import {debounceTime, distinctUntilChanged} from 'rxjs';
import {environment} from '@environment';

@Component({
  selector: 'app-list-of-administrators',
  templateUrl: './list-of-administrators.component.html',
  styleUrls: ['./list-of-administrators.component.scss']
})
export class ListOfAdministratorsComponent extends AbstractComponent implements OnInit {
  @Input() administrators: UserDetail[] = [];
  @ViewChild('deleteDialog') deleteDialog: TemplateRef<UserDetail> = {} as TemplateRef<UserDetail>;
  filteredAdmins: UserDetail[] = [];
  currentUserDetails: UserDetail | undefined;
  name: string = "";

  constructor(private translocoService: TranslocoService, private modalService: NgbModal,
              private administratorService: AdministratorsService, private userDetailService: UserDetailService, private toastService: ToastService,
              private route: ActivatedRoute, private exportService: ExportService) {
    super();
  }

  ngOnInit() {
    this.subscribeToRouteDataChange();
    this.subscriptions.push(this.userDetailService.currentUserDetails.subscribe((userDetail: UserDetail | undefined) => {
      this.currentUserDetails = userDetail;
    }));
  }

  openAddNewAdminUserModal() {
    const modalRef = this.modalService.open(AddUserModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate("administrator.list.add.new");
    modalRef.componentInstance.isAdmin = true;
    modalRef.result.then((result: AddUserResult) => {
      this.onAddAdministrator(result.email!);
    }, (_) => {
      /// ignore
    });
  }

  subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
        distinctUntilChanged()).subscribe((queryParams: any) => {
        this.name = queryParams.name ? queryParams.name : "";
        this.filterAdministrators();
      })
    );
  }

  filterAdministrators() {
    let filtered: UserDetail[] = this.administrators.filter((admin: UserDetail) => this.filterFunction(admin));
    this.filteredAdmins = filtered.length > 0 ? filtered : [];
  }

  filterFunction(admin: UserDetail): boolean {
    if (this.name) {
      let filterName: string = this.name.toLowerCase() ?? "";
      return !!(admin.firstname?.toLowerCase().includes(filterName) || admin.lastname?.toLowerCase().includes(filterName) ||
        admin.email?.toLowerCase().includes(filterName));
    }
    return true;
  }

  onAddAdministrator(email: string): void {
    this.administratorService.addAdministrator(email).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("administrator.list.add.success"));
        this.administratorService.findAdministrators(true).subscribe((users: UserDetail[]) => {
          this.administrators = users;
        })
      } else {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("administrator.list.add.error"));
      }
    })
  }

  openDeleteAdministratorDialog(user: UserDetail) {
    if (user.userId !== this.currentUserDetails?.userId) {
      const modalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
      });
      modalRef.componentInstance.title = this.translocoService.translate("administrator.list.delete.dialog.title");
      modalRef.componentInstance.text = this.translocoService.translate("administrator.list.delete.dialog.text");

      modalRef.result.then((_) => {
        this.onDeleteAdministrator(user);
      }, (_) => {
        /// ignore
      });
    }

  }

  onDeleteAdministrator(user: UserDetail): void {
    this.administratorService.deleteAdministrator(user.userId!).subscribe((result: boolean) => {
      if (result) {
        this.toastService.showToast(TOAST_STATE.success,
          this.translocoService.translate("administrator.list.delete.success"));
        this.administratorService.findAdministrators(true).subscribe((users: UserDetail[]) => {
          this.administrators = users;
        })
      } else {
        this.toastService.showToast(TOAST_STATE.danger,
          this.translocoService.translate("administrator.list.delete.error"));
      }
    })
  }

  downloadAdmins(type: string) {
    this.exportService.downloadAdmins(type, this.createFilter());
  }

  createFilter() {
    return FindOrganisersRequest.createOrganisersRequest({text: this.name !== "" ? this.name : undefined});
  }
}
